import { createContext, useContext, useEffect, useId, useMemo, useState } from "react";
import { Sun, Sprout, Droplets, Lock, ArrowUpRight, X, Loader, CheckCircle2 } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { track } from "@/lib/analytics";
import { useReveal } from "@/hooks/use-fx";
import { useModalA11y } from "@/hooks/use-modal-a11y";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import solarVillage from "@/assets/story/solar-village.jpg.asset.json";
import waterField from "@/assets/story/water-field.jpg.asset.json";
import storyFarmer from "@/assets/story/farmer.jpg.asset.json";

/* ------------------------------------------------------------------ */
/* Accent context — lets the Nav swap accent based on the active card */
/* ------------------------------------------------------------------ */

export type EcosystemAccent = {
  id: "default" | "solar" | "planty" | "aqua";
  label: string;
  /** OKLCH color tokens (h/s tuned for dark theme glassmorphism) */
  color: string;
  /** background gradient for glow effects */
  glow: string;
};

const DEFAULT_ACCENT: EcosystemAccent = {
  id: "default",
  label: "SFN",
  color: "oklch(0.82 0.18 75)", // saffron
  glow: "linear-gradient(135deg, oklch(0.82 0.18 75 / 0.45), oklch(0.74 0.19 30 / 0.4))",
};

type AccentCtx = {
  accent: EcosystemAccent;
  setAccent: (a: EcosystemAccent | null) => void;
};

const Ctx = createContext<AccentCtx>({ accent: DEFAULT_ACCENT, setAccent: () => {} });

export function EcosystemAccentProvider({ children }: { children: React.ReactNode }) {
  const [accent, setAccentState] = useState<EcosystemAccent>(DEFAULT_ACCENT);
  const value = useMemo<AccentCtx>(
    () => ({
      accent,
      setAccent: (a) => setAccentState(a ?? DEFAULT_ACCENT),
    }),
    [accent],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useEcosystemAccent() {
  return useContext(Ctx);
}

/* ------------------------------------------------------------------ */
/* Module definitions — placeholders ready for live activation        */
/* ------------------------------------------------------------------ */

type ModuleDef = {
  key: "solar" | "planty" | "aqua";
  name: string;
  tag: string;
  descKey: string;
  dependencyKey: string;
  icon: typeof Sun;
  accent: EcosystemAccent;
  /** Warm, human-centric photo shown at the top of the card. */
  image: string;
  imageAltKey: string;
  /** Reserved for future activation — wire real values when modules launch. */
  mintAddress: string | null;
  raydiumPoolId: string | null;
};

const MODULES: ModuleDef[] = [
  {
    key: "solar",
    name: "SolarPando",
    tag: "Energy Layer",
    descKey: "vp.solar.desc",
    dependencyKey: "vp.solar.dependency",
    icon: Sun,
    image: solarVillage.url,
    imageAltKey: "vp.solar.imgAlt",
    accent: {
      id: "solar",
      label: "SolarPando",
      color: "oklch(0.82 0.18 75)",
      glow: "linear-gradient(135deg, oklch(0.82 0.18 75 / 0.45), oklch(0.74 0.19 30 / 0.4))",
    },
    mintAddress: null,
    raydiumPoolId: null,
  },
  {
    key: "planty",
    name: "PlantyPando",
    tag: "Agriculture Layer",
    descKey: "vp.planty.desc",
    dependencyKey: "vp.planty.dependency",
    icon: Sprout,
    image: storyFarmer.url,
    imageAltKey: "vp.planty.imgAlt",
    accent: {
      id: "planty",
      label: "PlantyPando",
      color: "oklch(0.86 0.16 165)",
      glow: "linear-gradient(135deg, oklch(0.86 0.16 165 / 0.45), oklch(0.78 0.18 150 / 0.4))",
    },
    mintAddress: null,
    raydiumPoolId: null,
  },
  {
    key: "aqua",
    name: "AquaPando",
    tag: "Hydration Layer",
    descKey: "vp.aqua.desc",
    dependencyKey: "vp.aqua.dependency",
    icon: Droplets,
    image: waterField.url,
    imageAltKey: "vp.aqua.imgAlt",
    accent: {
      id: "aqua",
      label: "AquaPando",
      color: "oklch(0.78 0.14 230)",
      glow: "linear-gradient(135deg, oklch(0.78 0.14 230 / 0.45), oklch(0.7 0.16 245 / 0.4))",
    },
    mintAddress: null,
    raydiumPoolId: null,
  },
];

/* ------------------------------------------------------------------ */
/* Section                                                             */
/* ------------------------------------------------------------------ */

export function VillagePandoEcosystem() {
  const { t, lang } = useI18n();
  const r = useReveal<HTMLDivElement>();
  const [openModule, setOpenModule] = useState<ModuleDef | null>(null);
  const { setAccent } = useEcosystemAccent();

  return (
    <section id="villagepando" className="relative py-24 sm:py-32">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(60% 45% at 50% 0%, oklch(0.82 0.18 75 / 0.06), transparent 60%)",
        }}
      />
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div ref={r} className="reveal mx-auto max-w-2xl text-center">
          <span className="text-[11px] font-medium uppercase tracking-[0.2em] text-saffron">
            // {t("vp.eyebrow")}
          </span>
          <h2 className="mt-3 font-display text-4xl font-medium leading-[1.05] tracking-tight sm:text-5xl">
            {t("vp.title")}
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-muted-foreground">{t("vp.sub")}</p>
        </div>

        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {MODULES.map((m, i) => (
            <ModuleCard
              key={m.key}
              module={m}
              index={i}
              onEnter={() => setAccent(m.accent)}
              onLeave={() => setAccent(null)}
              onOpen={() => {
                setAccent(m.accent);
                setOpenModule(m);
                track("cta_click", { id: `vp_card_${m.key}`, surface: "villagepando" });
              }}
            />
          ))}
        </div>
      </div>

      <WhitelistModal
        module={openModule}
        lang={lang}
        onClose={() => {
          setOpenModule(null);
          setAccent(null);
        }}
      />
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Card                                                                */
/* ------------------------------------------------------------------ */

function ModuleCard({
  module: m,
  index,
  onEnter,
  onLeave,
  onOpen,
}: {
  module: ModuleDef;
  index: number;
  onEnter: () => void;
  onLeave: () => void;
  onOpen: () => void;
}) {
  const { t } = useI18n();
  const Icon = m.icon;

  return (
    <article
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onFocus={onEnter}
      onBlur={onLeave}
      className="reveal group relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.015] p-7 transition-shadow duration-500 hover:shadow-[var(--shadow-elev)]"
      style={{ animationDelay: `${index * 120}ms` }}
    >
      {/* Glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full blur-3xl opacity-40 transition-opacity duration-500 group-hover:opacity-70"
        style={{ background: m.accent.glow }}
      />

      <div className="relative z-10 -mx-2 -mt-2 mb-5 overflow-hidden rounded-2xl">
        <img
          src={m.image}
          alt={t(m.imageAltKey)}
          width={1024}
          height={768}
          loading="lazy"
          decoding="async"
          className="h-36 w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, transparent 30%, oklch(0.16 0.02 260 / 0.85) 100%)",
          }}
        />
      </div>

      <header className="relative z-10 flex items-start justify-between gap-4">
        <div>
          <span
            className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.18em]"
            style={{
              borderColor: `${m.accent.color.replace(")", " / 0.35)")}`,
              color: m.accent.color,
              background: `${m.accent.color.replace(")", " / 0.06)")}`,
            }}
          >
            <Icon className="h-3 w-3" />
            {m.tag}
          </span>
          <h3 className="mt-3 font-display text-2xl font-semibold tracking-tight">{m.name}</h3>
        </div>
        <div
          className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl glass"
          style={{ boxShadow: `inset 0 0 0 1px ${m.accent.color.replace(")", " / 0.3)")}` }}
        >
          <Icon className="h-5 w-5" style={{ color: m.accent.color }} />
        </div>
      </header>

      <p className="relative z-10 mt-5 text-sm leading-relaxed text-muted-foreground">
        {t(m.descKey)}
      </p>

      <div
        className="relative z-10 mt-5 rounded-xl border p-3"
        style={{
          borderColor: `${m.accent.color.replace(")", " / 0.25)")}`,
          background: `${m.accent.color.replace(")", " / 0.04)")}`,
        }}
      >
        <div
          className="font-mono text-[9px] uppercase tracking-[0.18em]"
          style={{ color: m.accent.color }}
        >
          {t("vp.dependency")}
        </div>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
          {t(m.dependencyKey)}
        </p>
      </div>

      {/* Frosted lock overlay */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-20 flex items-end justify-center rounded-3xl bg-obsidian/30 backdrop-blur-md transition-[backdrop-filter,background] duration-500 group-hover:backdrop-blur-sm group-hover:bg-obsidian/20"
      >
        <div className="pointer-events-none mb-6 inline-flex items-center gap-2 rounded-full border border-white/15 bg-obsidian/70 px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-foreground/90 shadow-[0_8px_30px_rgba(0,0,0,0.45)]">
          <Lock className="h-3 w-3" style={{ color: m.accent.color }} />
          {t("vp.locked")}
        </div>
      </div>

      <button
        type="button"
        onClick={onOpen}
        aria-label={`${t("vp.cta")} — ${m.name}`}
        className="absolute inset-0 z-30 cursor-pointer rounded-3xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-saffron/60"
      >
        <span className="sr-only">{t("vp.cta")}</span>
      </button>

      {/* Visible CTA chip floats above the overlay */}
      <div className="pointer-events-none absolute right-5 top-5 z-30">
        <span
          className="inline-flex items-center gap-1 rounded-full bg-obsidian/70 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-foreground/80 transition-transform duration-300 group-hover:-translate-y-0.5"
          style={{ boxShadow: `inset 0 0 0 1px ${m.accent.color.replace(")", " / 0.35)")}` }}
        >
          {t("vp.earlyAccess")}
          <ArrowUpRight className="h-3 w-3" style={{ color: m.accent.color }} />
        </span>
      </div>
    </article>
  );
}

/* ------------------------------------------------------------------ */
/* Whitelist modal                                                     */
/* ------------------------------------------------------------------ */

function WhitelistModal({
  module: m,
  lang,
  onClose,
}: {
  module: ModuleDef | null;
  lang: string;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const open = !!m;
  const containerRef = useModalA11y({ open, onClose, analyticsId: `vp_modal_${m?.key ?? "none"}` });
  const titleId = useId();
  const [email, setEmail] = useState("");
  const [wallet, setWallet] = useState("");
  const [note, setNote] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "ok" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setEmail("");
      setWallet("");
      setNote("");
      setStatus("idle");
      setErrorMsg(null);
    }
  }, [open, m?.key]);

  if (!m) return null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("submitting");
    setErrorMsg(null);
    const { error } = await supabase.from("villagepando_whitelist").insert({
      module: m.key,
      email: email.trim(),
      wallet: wallet.trim() || null,
      note: note.trim() || null,
      lang,
    });
    if (error) {
      setStatus("error");
      setErrorMsg(error.message);
      return;
    }
    track("cta_click", { id: `vp_whitelist_${m.key}`, surface: "villagepando_modal" });
    setStatus("ok");
  };

  return (
    <div
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-obsidian/70 p-4 backdrop-blur-sm"
    >
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative w-full max-w-md overflow-hidden rounded-3xl border border-white/10 bg-obsidian/95 p-6 shadow-[var(--shadow-elev)] sm:p-8"
      >
        <div
          aria-hidden
          className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full blur-3xl opacity-50"
          style={{ background: m.accent.glow }}
        />
        <button
          type="button"
          onClick={onClose}
          aria-label={t("vp.modal.close")}
          className="absolute right-4 top-4 z-10 grid h-9 w-9 place-items-center rounded-full border border-white/10 bg-white/[0.04] text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-saffron/60"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="relative z-10">
          <span
            className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.18em]"
            style={{
              borderColor: `${m.accent.color.replace(")", " / 0.35)")}`,
              color: m.accent.color,
              background: `${m.accent.color.replace(")", " / 0.06)")}`,
            }}
          >
            <m.icon className="h-3 w-3" />
            {m.tag}
          </span>
          <h3
            id={titleId}
            className="mt-3 font-display text-2xl font-semibold tracking-tight"
          >
            {t("vp.modal.title").replace("{module}", m.name)}
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            {t("vp.modal.body")}
          </p>

          {status === "ok" ? (
            <div
              role="status"
              className="mt-6 flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-4"
            >
              <CheckCircle2
                className="mt-0.5 h-5 w-5 shrink-0"
                style={{ color: m.accent.color }}
              />
              <div>
                <div className="font-display text-base font-semibold">
                  {t("vp.modal.success.title")}
                </div>
                <div className="mt-1 text-sm text-muted-foreground">
                  {t("vp.modal.success.body")}
                </div>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-3">
              <Field
                id="vp-email"
                type="email"
                required
                label={t("vp.modal.email")}
                placeholder="you@domain.com"
                value={email}
                onChange={setEmail}
                accent={m.accent.color}
              />
              <Field
                id="vp-wallet"
                label={t("vp.modal.wallet")}
                placeholder="Solana address (optional)"
                value={wallet}
                onChange={setWallet}
                accent={m.accent.color}
              />
              <Field
                id="vp-note"
                label={t("vp.modal.note")}
                placeholder={t("vp.modal.notePlaceholder")}
                value={note}
                onChange={setNote}
                accent={m.accent.color}
                textarea
              />

              {errorMsg && (
                <p className="text-xs text-coral" role="alert">
                  {errorMsg}
                </p>
              )}

              <button
                type="submit"
                disabled={status === "submitting" || !email}
                className={cn(
                  "liquid-btn mt-2 inline-flex w-full items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-bold uppercase tracking-wide text-obsidian transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-obsidian focus-visible:ring-saffron disabled:opacity-60",
                )}
                style={{
                  background: `linear-gradient(135deg, ${m.accent.color}, oklch(0.95 0.08 90))`,
                }}
              >
                {status === "submitting" ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin" />
                    {t("vp.modal.submitting")}
                  </>
                ) : (
                  t("vp.modal.submit")
                )}
              </button>

              <p className="pt-2 font-mono text-[9px] uppercase tracking-[0.18em] text-muted-foreground">
                {t("vp.modal.privacy")}
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({
  id,
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  required,
  textarea,
  accent,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  required?: boolean;
  textarea?: boolean;
  accent: string;
}) {
  const common = {
    id,
    value,
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      onChange(e.target.value),
    placeholder,
    required,
    className:
      "w-full rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2.5 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground/50 focus:border-white/30 focus-visible:ring-2",
    style: { ["--tw-ring-color" as string]: `${accent.replace(")", " / 0.5)")}` } as React.CSSProperties,
  };
  return (
    <div>
      <label
        htmlFor={id}
        className="block font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground"
      >
        {label}
      </label>
      <div className="mt-1.5">
        {textarea ? (
          <textarea rows={3} {...common} />
        ) : (
          <input type={type} {...common} />
        )}
      </div>
    </div>
  );
}
